import * as React from "react";
import { useTransition, useSpring, config, animated } from "react-spring";
import classNames from "classnames";
import { Tooltip } from "react-tooltip";
import { useDrag } from "@use-gesture/react";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import useStyles from "./FloatingDebugPanel.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { Panel } from "./panels/Panel";

const AnimatedFontAwesomeIcon = animated(FontAwesomeIcon);

interface ContainerOptions {
    open?: boolean;
    xy?: [number, number];
}

const makeContainerProps = ({ xy: [x, y] = [0, 0] }: ContainerOptions = {}) => ({
    config: config.stiff,
    immediate: (name: string) => name === "top" || name === "left",
    margin: 60,
    top: y,
    left: x,
});

interface Props {
    title: string;
    panels: Panel[];
    selected: string;
    onSelectedChanged(selected: string): void;
}

export default function FloatingDebugPanel({ title, panels, selected, onSelectedChanged }: Props) {
    const [open, setOpen] = React.useState(false);
    const classes = useStyles({ selected, open } as any);
    const { width, height } = useWindowDimensions();

    const [containerProps, setContainerProps] = useSpring(() => makeContainerProps());
    const buttonRef = React.useRef<HTMLDivElement | null>(null);
    const settingsButtonRef = React.useRef<HTMLDivElement | null>(null);
    const gestureStateRef = React.useRef<any>();

    React.useEffect(() => {
        if (gestureStateRef.current) {
            const props = makeContainerProps({ xy: gestureStateRef.current.offset });
            setContainerProps(props);
        }
    }, [gestureStateRef, setContainerProps]);

    const bind = useDrag(
        (gs: any) => {
            const { tap, offset, event } = gs;
            gestureStateRef.current = gs;
            const target = event.target as Element;
            if (tap && settingsButtonRef.current?.contains(target)) {
                setOpen(true);
                onSelectedChanged("settings");
                return;
            }
            if (tap && buttonRef.current?.contains(target)) {
                setOpen((o) => !o);
            } else {
                const props = makeContainerProps({ xy: offset });
                setContainerProps(props);
            }
        },
        {
            bounds: { top: -60, left: -60, right: width - 180, bottom: height - 280 },
            filterTaps: true,
        },
    );

    const selectedPanel = panels.find((p) => p.id === selected);

    const panelTransitions = useTransition(open ? [selectedPanel] : [], {
        config: config.stiff,
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });

    const panelBtnTransitions = useTransition(open ? panels : [], {
        keys: (p: any) => p.id,
        config: config.stiff,
        unique: true,
        trail: 200 / panels.length,
        from: { opacity: 0, transform: "scale(0)" },
        enter: { opacity: 1, transform: "scale(1)" },
        leave: { opacity: 0, transform: "scale(0)" },
    });

    const toggleBtnTransitions = useTransition(open, {
        from: { opacity: 0, transform: "scale(0)" },
        initial: { opacity: 1, transform: "scale(1)" },
        enter: { opacity: 1, transform: "scale(1)" },
        leave: { opacity: 0, transform: "scale(0)" },
    });

    return (
        <animated.div className={classes.floatingContainer} style={containerProps}>
            <div className={classes.toolbar} {...bind()}>
                <div className={classes.widgetToggleButton} ref={buttonRef}>
                    {toggleBtnTransitions((style, item) =>
                        item ? (
                            <AnimatedFontAwesomeIcon
                                icon={["far", "chevron-up"]}
                                size="xs"
                                className={classes.widgetToggleButtonIcon}
                                style={style}
                            />
                        ) : (
                            <AnimatedFontAwesomeIcon
                                icon={["fas", "vial"]}
                                className={classes.widgetToggleButtonIcon}
                                style={style}
                            />
                        ),
                    )}
                </div>
                <div className={classes.titleContainer}>
                    <h3 className={classes.title}>{title}</h3>
                    <div
                        ref={settingsButtonRef}
                        className={classes.settingsButton}
                        onClick={() => {
                            setOpen(true);
                            onSelectedChanged("settings");
                        }}
                    >
                        <FontAwesomeIcon
                            size="lg"
                            icon={["fas", "cog"]}
                            transform="shrink-5"
                            mask={["fas", "circle"]}
                        />
                    </div>
                </div>
            </div>
            <div className={classes.panelButtonContainer}>
                {panelBtnTransitions((style, item) => {
                    if (item.exclude) return null;
                    const { onClick, id, icon, active, title } = item;
                    return (
                        <animated.div
                            key={id}
                            data-tip={title}
                            style={style}
                            onClick={onClick || (() => onSelectedChanged(id))}
                            className={classNames(
                                classes.panelButton,
                                active && classes.panelButtonActive,
                            )}
                        >
                            <div className={classes.panelButtonIcon}>{icon}</div>
                        </animated.div>
                    );
                })}
            </div>
            {panelTransitions((style, item) => {
                if (!item) return null;
                const { content, index = 0 } = item;
                return (
                    <animated.div key={item.id} className={classes.panel} style={style}>
                        <>
                            <div
                                style={{ top: 40 + index * 56 }}
                                className={classes.panelIndicatorShadow}
                            />
                            <div
                                style={{ top: 42 + index * 56 }}
                                className={classes.panelIndicator}
                            />
                        </>
                        <div className={classes.panelContainer}>
                            <animated.div className={classes.panelContent}>{content}</animated.div>
                        </div>
                    </animated.div>
                );
            })}
            <Tooltip />
        </animated.div>
    );
}
